import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Popconfirm,
  Upload,
  Alert,
  Tooltip,
  Collapse,
  Row,
  Col,
  Tag,
  Pagination,
  TreeSelect,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
  SyncOutlined,
  FolderOpenOutlined,
  UserOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import * as classService from '../../services/classService';
import courseService from '../../services/courseService';
import nganhHocService from '../../services/nganhHocService';
import sinhVienService from '../../services/sinhVienService';
import moodleApiService, { getEnrolledUsers, unenrolUser, updateUserEnrolmentStatus } from '../../services/moodleService';
import type { Class, Course, NganhHoc, SinhVien, ClassCreateRequest, ClassUpdateRequest } from '../../../../shared/types';

const { Option } = Select;

interface ClassFormData {
  LopHocID: string;
  TenLop: string;
  KhoaHocID: string;
  NganhHocID?: string;
}

interface GroupedClasses {
  [courseId: string]: {
    courseName: string;
    classes: Class[];
  };
}

const ClassManagementPage: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [nganhHocs, setNganhHocs] = useState<NganhHoc[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [editingClass, setEditingClass] = useState<Class | null>(null);
  const [form] = Form.useForm();
  const [syncForm] = Form.useForm();

  // Pagination and filters
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [selectedNganhHocId, setSelectedNganhHocId] = useState('');

  // Import states
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  // Accordion state
  const [activeKey, setActiveKey] = useState<string[]>([]);

  // Student modal states
  const [studentModalVisible, setStudentModalVisible] = useState(false);
  const [studentsInClass, setStudentsInClass] = useState<SinhVien[]>([]);
  const [currentClass, setCurrentClass] = useState<Class | null>(null);

  // Moodle Enrolment Modal states
  const [enrolModalVisible, setEnrolModalVisible] = useState(false);
  const [moodleCourseTree, setMoodleCourseTree] = useState<any[]>([]);
  const [selectedMoodleCourse, setSelectedMoodleCourse] = useState<number | null>(null);
  const [loadingMoodleTree, setLoadingMoodleTree] = useState(false);

  // Moodle Enrolled Users Modal states
  const [enrolledUsersModalVisible, setEnrolledUsersModalVisible] = useState(false);
  const [enrolledUsers, setEnrolledUsers] = useState<any[]>([]);
  const [loadingEnrolledUsers, setLoadingEnrolledUsers] = useState(false);
  const [selectedCourseForUsers, setSelectedCourseForUsers] = useState<any>(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadClasses();
    loadCourses();
    loadNganhHoc();
  }, [currentPage, pageSize, searchText, selectedCourseId, selectedNganhHocId]);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('quanly_truonghoc_token');
      if (!token) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        navigate('/login');
        return;
      }

      const response = await classService.listClasses({
        page: currentPage,
        limit: pageSize,
        search: searchText,
        khoaHocId: selectedCourseId,
        nganhHocId: selectedNganhHocId,
      });
      setClasses(response.data);
      setTotal(response.pagination.totalItems);
    } catch (error: any) {
      console.error('Error loading classes:', error);
      
      if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        localStorage.removeItem('quanly_truonghoc_token');
        localStorage.removeItem('quanly_truonghoc_user');
        navigate('/login');
      } else {
        const errorMsg = error.response?.data?.message || 'Không thể tải danh sách lớp học';
        message.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await courseService.getCourses({ page: 1, limit: 1000 });
      setCourses(response.data);
    } catch (error: any) {
      console.error('Error loading courses:', error);
      
      if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        localStorage.removeItem('quanly_truonghoc_token');
        localStorage.removeItem('quanly_truonghoc_user');
        navigate('/login');
      } else {
        const errorMsg = error.response?.data?.message || 'Không thể tải danh sách khóa học';
        message.error(errorMsg);
      }
    }
  };

  const loadNganhHoc = async () => {
    try {
      const response = await nganhHocService.getNganhHoc({ page: 1, limit: 1000 });
      setNganhHocs(response.data);
    } catch (error: any) {
      console.error('Error loading NganhHoc:', error);
      
      if (error.response?.status === 401) {
        message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
        localStorage.removeItem('quanly_truonghoc_token');
        localStorage.removeItem('quanly_truonghoc_user');
        navigate('/login');
      } else {
        const errorMsg = error.response?.data?.message || 'Không thể tải danh sách ngành học';
        message.error(errorMsg);
      }
    }
  };

  const handleCreate = () => {
    setEditingClass(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (classItem: Class) => {
    setEditingClass(classItem);
    form.setFieldsValue(classItem);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await classService.deleteClass(id);
      message.success('Xóa lớp học thành công');
      loadClasses();
    } catch (error: any) {
      console.error('Error deleting class:', error);
      const errorMessage = error.response?.data?.error || 'Không thể xóa lớp học';
      message.error(errorMessage);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData: ClassFormData = values;

      if (editingClass) {
        const updateData: ClassUpdateRequest = {
          TenLop: formData.TenLop,
          KhoaHocID: formData.KhoaHocID,
          NganhHocID: formData.NganhHocID
        };
        await classService.updateClass(editingClass.LopHocID, updateData);
        message.success('Cập nhật lớp học thành công');
      } else {
        const createData: ClassCreateRequest = formData;
        await classService.createClass(createData);
        message.success('Tạo lớp học thành công');
      }

      setModalVisible(false);
      loadClasses();
    } catch (error: any) {
      console.error('Error saving class:', error);
      const errorMessage = error.response?.data?.error || 'Không thể lưu lớp học';
      message.error(errorMessage);
    }
  };

  const handleViewStudents = async (classItem: Class) => {
    setCurrentClass(classItem);
    setLoading(true);
    try {
      const response = await sinhVienService.getByClass(classItem.LopHocID, { limit: 1000 }); // Get all students
      setStudentsInClass(response.data);
      setStudentModalVisible(true);
    } catch (error) {
      message.error('Không thể tải danh sách sinh viên');
    } finally {
      setLoading(false);
    }
  };

  const openEnrolModal = async (classItem: Class) => {
    setCurrentClass(classItem);
    setSelectedMoodleCourse(null);
    setEnrolModalVisible(true);
    
    // Load Moodle course tree
    setLoadingMoodleTree(true);
    try {
      const response = await moodleApiService.getCourseTree();
      if (response && response.success) {
        setMoodleCourseTree(response.data || []);
      } else {
        message.error('Không thể tải danh sách khóa học từ Moodle');
        setMoodleCourseTree([]);
      }
    } catch (error) {
      message.error('Lỗi khi tải cây khóa học Moodle');
      setMoodleCourseTree([]);
    } finally {
      setLoadingMoodleTree(false);
    }
  };
  
  const handleEnrolMoodle = async () => {
    if (!currentClass || !selectedMoodleCourse) {
      message.error('Vui lòng chọn một khóa học Moodle');
      return;
    }

    try {
      setLoading(true);
      const result = await classService.enrolMoodle(currentClass.LopHocID, selectedMoodleCourse);
      if (result.success) {
        message.success(result.message);
        setEnrolModalVisible(false);
      } else {
        message.error(result.error || 'Ghi danh thất bại');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Không thể ghi danh';
      const errorDetails = error.response?.data?.details;
      
      message.error(
        <span>
          {errorMessage}
          {errorDetails && <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.8 }}>Chi tiết: {errorDetails}</div>}
        </span>,
        10
      );
    } finally {
      setLoading(false);
    }
  };

  const openEnrolledUsersModal = async () => {
    if (!selectedMoodleCourse) {
      message.error('Vui lòng chọn một khóa học Moodle trước');
      return;
    }

    setLoadingEnrolledUsers(true);
    setEnrolledUsersModalVisible(true);
    
    try {
      const response = await getEnrolledUsers(selectedMoodleCourse);
      if (response && response.success) {
        setEnrolledUsers(response.data || []);
        // Find course info for display
        const findCourseInTree = (nodes: any[]): any => {
          for (const node of nodes) {
            if (node.value === selectedMoodleCourse) {
              return node;
            }
            if (node.children) {
              const found = findCourseInTree(node.children);
              if (found) return found;
            }
          }
          return null;
        };
        const courseInfo = findCourseInTree(moodleCourseTree);
        setSelectedCourseForUsers(courseInfo);
      } else {
        message.error('Không thể tải danh sách sinh viên đã ghi danh');
        setEnrolledUsers([]);
      }
    } catch (error) {
      message.error('Lỗi khi tải danh sách sinh viên đã ghi danh');
      setEnrolledUsers([]);
    } finally {
      setLoadingEnrolledUsers(false);
    }
  };

  const handleUnenrolUser = async (userId: number, userFullName: string) => {
    if (!selectedMoodleCourse) {
      message.error('Không xác định được khóa học');
      return;
    }

    try {
      setLoadingEnrolledUsers(true);
      const result = await unenrolUser(selectedMoodleCourse, userId);
      if (result && result.success) {
        message.success(`Đã hủy ghi danh cho ${userFullName}`);
        // Reload enrolled users list
        const response = await getEnrolledUsers(selectedMoodleCourse);
        if (response && response.success) {
          setEnrolledUsers(response.data || []);
        }
      } else {
        message.error(result?.error || 'Không thể hủy ghi danh');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Lỗi khi hủy ghi danh';
      message.error(errorMessage);
    } finally {
      setLoadingEnrolledUsers(false);
    }
  };

  const handleUpdateEnrolmentStatus = async (userId: number, userFullName: string, currentStatus: number) => {
    if (!selectedMoodleCourse) {
      message.error('Không xác định được khóa học');
      return;
    }

    const newStatus = currentStatus === 0 ? 1 : 0; // Toggle status
    const statusText = newStatus === 0 ? 'kích hoạt' : 'tạm ngừng';

    try {
      setLoadingEnrolledUsers(true);
      const result = await updateUserEnrolmentStatus(selectedMoodleCourse, userId, newStatus);
      if (result && result.success) {
        message.success(`Đã ${statusText} ghi danh cho ${userFullName}`);
        // Reload enrolled users list
        const response = await getEnrolledUsers(selectedMoodleCourse);
        if (response && response.success) {
          setEnrolledUsers(response.data || []);
        }
      } else {
        message.error(result?.error || `Không thể ${statusText} ghi danh`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || `Lỗi khi ${statusText} ghi danh`;
      message.error(errorMessage);
    } finally {
      setLoadingEnrolledUsers(false);
    }
  };

  const handleSync = async () => {
    try {
      const values = await syncForm.validateFields();
      const result = await classService.syncClasses({ KhoaHocID: values.KhoaHocID });
      
      message.success(
        `Đồng bộ thành công: ${result.added} lớp học mới, ${result.updated} lớp học cập nhật`
      );
      setSyncModalVisible(false);
      loadClasses();
    } catch (error: any) {
      console.error('Error syncing classes:', error);
      const errorMessage = error.response?.data?.error || 'Không thể đồng bộ lớp học';
      message.error(errorMessage);
    }
  };

  const downloadTemplate = () => {
    classService.downloadClassTemplate();
  };

  const handleImport = async () => {
    if (fileList.length === 0) {
      message.error('Vui lòng chọn file Excel');
      return;
    }

    const file = fileList[0];
    try {
      // Read file and parse Excel data
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          // For now, let's show error that Excel parsing is not implemented
          message.error('Chức năng import Excel chưa được triển khai đầy đủ. Vui lòng sử dụng CSV template.');
        } catch (parseError) {
          message.error('Không thể đọc file Excel');
        }
      };
      reader.readAsArrayBuffer(file.originFileObj as File);
      
      setImportModalVisible(false);
      setFileList([]);
    } catch (error: any) {
      console.error('Error importing classes:', error);
      const errorMessage = error.response?.data?.error || 'Không thể import lớp học';
      message.error(errorMessage);
    }
  };

  // Group classes by course
  const groupedClasses = classes.reduce<GroupedClasses>((acc, classItem) => {
    const courseId = classItem.KhoaHocID;
    if (!acc[courseId]) {
      const course = courses.find(c => c.KhoaHocID === courseId);
      acc[courseId] = {
        courseName: course?.TenKhoaHoc || courseId,
        classes: []
      };
    }
    acc[courseId].classes.push(classItem);
    return acc;
  }, {});

  const columns: ColumnsType<Class> = [
    {
      title: 'Mã lớp',
      dataIndex: 'LopHocID',
      key: 'LopHocID',
      width: 120,
    },
    {
      title: 'Tên lớp',
      dataIndex: 'TenLop',
      key: 'TenLop',
    },
    {
      title: 'Khóa học',
      dataIndex: 'KhoaHocID',
      key: 'KhoaHocID',
      width: 100,
      render: (khoaHocId: string) => {
        const course = courses.find((c) => c.KhoaHocID === khoaHocId);
        return course?.TenKhoaHoc || khoaHocId;
      },
    },
    {
      title: 'Ngành học',
      dataIndex: 'NganhHocID',
      key: 'NganhHocID',
      width: 150,
      render: (nganhHocId?: string) => {
        if (!nganhHocId) return <Tag color="default">Không xác định</Tag>;
        const nganhHoc = nganhHocs.find(
          (n: NganhHoc) => n.NganhHocID === nganhHocId
        );
        return <Tag color="blue">{nganhHoc?.TenNganh || nganhHocId}</Tag>;
      },
    },
    {
      title: 'Hành động',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Sửa">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Ghi danh Moodle">
            <Button
              icon={<UsergroupAddOutlined />}
              size="small"
              onClick={() => openEnrolModal(record)}
            />
          </Tooltip>
          <Tooltip title="Xem sinh viên">
            <Button
              icon={<UserOutlined />}
              size="small"
              onClick={() => handleViewStudents(record)}
            />
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa lớp học này?"
              onConfirm={() => handleDelete(record.LopHocID)}
              okText="Xóa"
              cancelText="Hủy"
            >
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const collapseItems = Object.entries(groupedClasses).map(
    ([courseId, { courseName, classes: courseClasses }], index) => ({
      key: `collapse-${courseId}-${index}`,
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span style={{ fontWeight: 500, textAlign: 'left' }}>
            {courseName} ({courseId})
          </span>
          <Tag color="blue">{courseClasses.length} lớp</Tag>
        </div>
      ),
      children: (
        <Table<Class>
          columns={columns}
          dataSource={courseClasses}
          rowKey={(record) => `table-${record.LopHocID}-${courseId}`}
          pagination={false}
          size="small"
          bordered
        />
      ),
    })
  );

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <h2 style={{ margin: 0, textAlign: 'left' }}>Quản lý lớp học</h2>
            </Col>
            <Col>
              <Space wrap>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleCreate}
                >
                  Thêm lớp học
                </Button>
                <Button 
                  icon={<DownloadOutlined />} 
                  onClick={downloadTemplate}
                >
                  Tải file mẫu
                </Button>
                <Button 
                  icon={<UploadOutlined />} 
                  onClick={() => setImportModalVisible(true)}
                >
                  Import Excel
                </Button>
                <Button 
                  icon={<SyncOutlined />} 
                  onClick={() => setSyncModalVisible(true)}
                >
                  Đồng bộ lớp học
                </Button>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={loadClasses}
                  loading={loading}
                >
                  Làm mới
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {/* Filter Section */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Input.Search
                placeholder="Tìm kiếm lớp học..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={loadClasses}
                allowClear
              />
            </Col>
            <Col span={6}>
              <Select
                placeholder="Chọn khóa học"
                value={selectedCourseId}
                onChange={setSelectedCourseId}
                allowClear
                style={{ width: '100%' }}
              >
                {courses.map((course, index) => (
                  <Option key={`filter-course-${course.KhoaHocID}-${index}`} value={course.KhoaHocID}>
                    {course.TenKhoaHoc}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <Select
                placeholder="Chọn ngành học"
                value={selectedNganhHocId}
                onChange={setSelectedNganhHocId}
                allowClear
                style={{ width: '100%' }}
              >
                {nganhHocs.map((nganh: NganhHoc, index) => (
                  <Option key={`filter-nganh-${nganh.NganhHocID}-${index}`} value={nganh.NganhHocID}>
                    {nganh.TenNganh}
                  </Option>
                ))}
              </Select>
            </Col>
          </Row>
        </div>

        {/* Accordion Grouping */}
        <Collapse
          activeKey={activeKey}
          onChange={(keys) => setActiveKey(Array.isArray(keys) ? keys : [keys])}
          expandIcon={({ isActive }) => <FolderOpenOutlined rotate={isActive ? 90 : 0} />}
          items={collapseItems}
        />

        {/* Pagination */}
        <div style={{ 
          marginTop: 16, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <div style={{ textAlign: 'left' }}>
            Hiển thị {Math.min((currentPage - 1) * pageSize + 1, total)} - {Math.min(currentPage * pageSize, total)} trong tổng số {total} lớp học
          </div>
          <Pagination
            current={currentPage}
            total={total}
            pageSize={pageSize}
            onChange={(page, size) => {
              setCurrentPage(page);
              if (size !== pageSize) {
                setPageSize(size);
              }
            }}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) => `${range[0]}-${range[1]} của ${total} mục`}
            pageSizeOptions={['10', '25', '50', '100']}
          />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingClass ? 'Chỉnh sửa lớp học' : 'Thêm lớp học mới'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText={editingClass ? 'Cập nhật' : 'Tạo mới'}
        cancelText="Hủy"
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ NganhHocID: undefined }}
        >
          {!editingClass && (
            <Form.Item
              label="Mã lớp"
              name="LopHocID"
              rules={[
                { required: true, message: 'Vui lòng nhập mã lớp' },
                { pattern: /^[A-Za-z0-9]+$/, message: 'Mã lớp chỉ được chứa chữ và số' }
              ]}
            >
              <Input placeholder="Ví dụ: 1760212441" />
            </Form.Item>
          )}
          
          <Form.Item
            label="Tên lớp"
            name="TenLop"
            rules={[{ required: true, message: 'Vui lòng nhập tên lớp' }]}
          >
            <Input placeholder="Ví dụ: CD Y5K2A" />
          </Form.Item>

          <Form.Item
            label="Khóa học"
            name="KhoaHocID"
            rules={[{ required: true, message: 'Vui lòng chọn khóa học' }]}
          >
            <Select placeholder="Chọn khóa học">
              {courses.map((course, index) => (
                <Option key={`create-course-${course.KhoaHocID}-${index}`} value={course.KhoaHocID}>
                  {course.TenKhoaHoc}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Ngành học"
            name="NganhHocID"
          >
            <Select placeholder="Chọn ngành học (tùy chọn)" allowClear>
              {nganhHocs.map((nganh: NganhHoc, index) => (
                <Option key={`create-nganh-${nganh.NganhHocID}-${index}`} value={nganh.NganhHocID}>
                  {nganh.TenNganh}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Import Modal */}
      <Modal
        title="Import danh sách lớp học"
        open={importModalVisible}
        onOk={handleImport}
        onCancel={() => {
          setImportModalVisible(false);
          setFileList([]);
        }}
        okText="Import"
        cancelText="Hủy"
      >
        <div style={{ marginBottom: 16 }}>
          <Alert
            message="Hướng dẫn import"
            description="Vui lòng tải file mẫu và điền thông tin theo đúng định dạng. File hỗ trợ định dạng Excel (.xlsx) hoặc CSV."
            type="info"
            showIcon
          />
        </div>
        
        <Upload
          accept=".xlsx,.xls,.csv"
          fileList={fileList}
          onChange={({ fileList }) => setFileList(fileList)}
          beforeUpload={() => false}
          maxCount={1}
        >
          <Button icon={<UploadOutlined />}>Chọn file Excel</Button>
        </Upload>
      </Modal>

      {/* Sync Modal */}
      <Modal
        title="Đồng bộ lớp học từ hệ thống cũ"
        open={syncModalVisible}
        onOk={handleSync}
        onCancel={() => setSyncModalVisible(false)}
        okText="Đồng bộ"
        cancelText="Hủy"
      >
        <Alert
          message="Thông tin đồng bộ"
          description="Chức năng này sẽ đồng bộ dữ liệu lớp học từ hệ thống cũ. Các lớp học mới sẽ được thêm vào và thông tin lớp học đã có sẽ được cập nhật."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Form form={syncForm} layout="vertical">
          <Form.Item
            label="Khóa học cần đồng bộ"
            name="KhoaHocID"
            rules={[{ required: true, message: 'Vui lòng chọn khóa học' }]}
          >
            <Select placeholder="Chọn khóa học">
              {courses.map((course, index) => (
                <Option key={`sync-course-${course.KhoaHocID}-${index}`} value={course.KhoaHocID}>
                  {course.TenKhoaHoc}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Enrol Moodle Modal */}
      <Modal
        title={`Ghi danh lớp ${currentClass?.TenLop} vào khóa học Moodle`}
        open={enrolModalVisible}
        onOk={handleEnrolMoodle}
        onCancel={() => setEnrolModalVisible(false)}
        confirmLoading={loading}
        okText="Ghi danh"
        cancelText="Hủy"
        footer={[
          <Button key="view-users" onClick={openEnrolledUsersModal} disabled={!selectedMoodleCourse}>
            Xem danh sách đã ghi danh
          </Button>,
          <Button key="cancel" onClick={() => setEnrolModalVisible(false)}>
            Hủy
          </Button>,
          <Button key="enrol" type="primary" loading={loading} onClick={handleEnrolMoodle}>
            Ghi danh
          </Button>,
        ]}
      >
        <p>Chọn khóa học trên Moodle để ghi danh tất cả sinh viên từ lớp này vào.</p>
        <TreeSelect
          style={{ width: '100%' }}
          value={selectedMoodleCourse}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          treeData={moodleCourseTree}
          placeholder="Vui lòng chọn khóa học"
          treeDefaultExpandAll
          onChange={(newValue: any) => {
            // Only allow selecting courses (numeric values), not categories (string values starting with 'category-')
            if (typeof newValue === 'number' || (typeof newValue === 'string' && !newValue.startsWith('category-'))) {
              setSelectedMoodleCourse(Number(newValue));
            }
          }}
          loading={loadingMoodleTree}
          showSearch
          treeNodeFilterProp="title"
          filterTreeNode={(input, node) =>
            String(node?.title ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      </Modal>

      {/* Student List Modal */}
      <Modal
        title={
          currentClass &&
          `Danh sách sinh viên lớp ${currentClass.TenLop} (Sĩ số: ${studentsInClass.length})`
        }
        open={studentModalVisible}
        onCancel={() => setStudentModalVisible(false)}
        footer={null}
        width="80%"
        style={{ top: 20 }}
      >
        <Table
          dataSource={studentsInClass}
          rowKey={(record) => `student-${record.MaHS}-${currentClass?.LopHocID}`}
          loading={loading}
          pagination={{ pageSize: 15 }}
          columns={[
            { title: 'Mã HS', dataIndex: 'MaHS', key: 'student-MaHS' },
            { title: 'Họ tên', dataIndex: 'Hoten', key: 'student-Hoten' },
            {
              title: 'Ngày sinh',
              dataIndex: 'Ngaysinh',
              key: 'student-Ngaysinh',
              render: (text) => text ? new Date(text).toLocaleDateString('vi-VN') : '',
            },
            { title: 'Giới tính', dataIndex: 'Gioitinh', key: 'student-Gioitinh' },
            { title: 'Nơi sinh', dataIndex: 'NoiSinh', key: 'student-NoiSinh' },
          ]}
        />
      </Modal>

      {/* Enrolled Users Modal */}
      <Modal
        title={
          selectedCourseForUsers &&
          `Danh sách sinh viên đã ghi danh - ${selectedCourseForUsers.title} (${enrolledUsers.length} người)`
        }
        open={enrolledUsersModalVisible}
        onCancel={() => setEnrolledUsersModalVisible(false)}
        footer={null}
        width="90%"
        style={{ top: 20 }}
      >
        <Table
          dataSource={enrolledUsers}
          rowKey={(record) => `enrolled-${record.id}-${selectedMoodleCourse}`}
          loading={loadingEnrolledUsers}
          pagination={{ pageSize: 20 }}
          columns={[
            { 
              title: 'ID', 
              dataIndex: 'id', 
              key: 'enrolled-id',
              width: 80,
            },
            { 
              title: 'Username', 
              dataIndex: 'username', 
              key: 'enrolled-username',
              width: 150,
            },
            { 
              title: 'Họ tên', 
              key: 'enrolled-fullname',
              width: 200,
              render: (_, record) => `${record.firstname} ${record.lastname}`,
            },
            {
              title: 'Phòng/Ban (Lớp)',
              dataIndex: 'department',
              key: 'enrolled-department',
              width: 150,
            },
            { 
              title: 'Email', 
              dataIndex: 'email', 
              key: 'enrolled-email',
              width: 200,
            },
            {
              title: 'Trạng thái',
              key: 'enrolled-status',
              width: 120,
              render: (_, record) => {
                // Check if user has suspended enrollment
                const isSuspended = record.enrolledcourses?.some((course: any) => 
                  course.id === selectedMoodleCourse && course.suspended
                );
                return (
                  <Tag color={isSuspended ? 'red' : 'green'}>
                    {isSuspended ? 'Tạm ngừng' : 'Hoạt động'}
                  </Tag>
                );
              },
            },
            {
              title: 'Hành động',
              key: 'enrolled-actions',
              width: 200,
              render: (_, record) => (
                <Space size="small">
                  <Tooltip title="Hủy ghi danh">
                    <Popconfirm
                      title="Bạn có chắc chắn muốn hủy ghi danh cho người dùng này?"
                      onConfirm={() => handleUnenrolUser(record.id, `${record.firstname} ${record.lastname}`)}
                      okText="Hủy ghi danh"
                      cancelText="Không"
                    >
                      <Button 
                        type="primary" 
                        danger 
                        size="small"
                        icon={<DeleteOutlined />}
                      >
                        Hủy ghi danh
                      </Button>
                    </Popconfirm>
                  </Tooltip>
                  <Tooltip title="Thay đổi trạng thái">
                    <Button 
                      size="small"
                      onClick={() => {
                        const isSuspended = record.enrolledcourses?.some((course: any) => 
                          course.id === selectedMoodleCourse && course.suspended
                        );
                        handleUpdateEnrolmentStatus(
                          record.id, 
                          `${record.firstname} ${record.lastname}`,
                          isSuspended ? 1 : 0
                        );
                      }}
                    >
                      {record.enrolledcourses?.some((course: any) => 
                        course.id === selectedMoodleCourse && course.suspended
                      ) ? 'Kích hoạt' : 'Tạm ngừng'}
                    </Button>
                  </Tooltip>
                </Space>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default ClassManagementPage; 